import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Underline from '@tiptap/extension-underline'
import Link from '@tiptap/extension-link'
import Image from '@tiptap/extension-image'
import TextAlign from '@tiptap/extension-text-align'
import { TextStyle } from '@tiptap/extension-text-style'
import Color from '@tiptap/extension-color'
import Highlight from '@tiptap/extension-highlight'
import Placeholder from '@tiptap/extension-placeholder'
import { Table, TableRow, TableCell, TableHeader } from '@tiptap/extension-table'
import { useState, useCallback, useEffect } from 'react'
import { dashboardApi } from '../../lib/dashboardApi'

// ─── Toolbar button ───────────────────────────────────────────────────────────
function ToolBtn({ onClick, active, disabled, title, children }) {
  return (
    <button
      type="button"
      onMouseDown={e => { e.preventDefault(); onClick() }}
      disabled={disabled}
      title={title}
      className={`px-2 py-1 rounded text-sm transition-colors select-none
        ${active
          ? 'bg-[#358ECD] text-white'
          : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'}
        ${disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}
    >
      {children}
    </button>
  )
}

function Divider() {
  return <span className="w-px h-5 bg-gray-200 mx-1 shrink-0" />
}

// ─── Link modal ───────────────────────────────────────────────────────────────
function LinkModal({ onConfirm, onClose, initial = '' }) {
  const [href, setHref] = useState(initial)
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-xl p-4 w-80">
        <p className="text-sm font-semibold text-gray-700 mb-2">Insert Link</p>
        <input
          autoFocus
          type="url"
          value={href}
          onChange={e => setHref(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') onConfirm(href) }}
          placeholder="https://example.com"
          className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-[#358ECD]"
        />
        <div className="flex gap-2 justify-end">
          <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm rounded border border-gray-300 hover:bg-gray-50">Cancel</button>
          {initial && (
            <button type="button" onClick={() => onConfirm(null)} className="px-3 py-1.5 text-sm rounded bg-red-100 text-red-600 hover:bg-red-200">Remove</button>
          )}
          <button type="button" onClick={() => onConfirm(href)} className="px-3 py-1.5 text-sm rounded bg-[#358ECD] text-white hover:bg-[#2d7ab5]">OK</button>
        </div>
      </div>
    </div>
  )
}

// ─── Image modal — supports file upload (with preview) and URL ────────────────
function ImageModal({ onConfirm, onClose }) {
  const [tab, setTab]         = useState('upload')   // 'upload' | 'url'
  const [src, setSrc]         = useState('')          // URL tab
  const [alt, setAlt]         = useState('')
  const [preview, setPreview] = useState(null)        // upload tab: data-URL
  const [fileName, setFileName] = useState('')

  const handleFileChange = (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    setFileName(file.name)
    const reader = new FileReader()
    reader.onload = (ev) => setPreview(ev.target.result)
    reader.readAsDataURL(file)
  }

  const handleDrop = (e) => {
    e.preventDefault()
    const file = e.dataTransfer.files?.[0]
    if (!file || !file.type.startsWith('image/')) return
    setFileName(file.name)
    const reader = new FileReader()
    reader.onload = (ev) => setPreview(ev.target.result)
    reader.readAsDataURL(file)
  }

  const canInsert = tab === 'upload' ? !!preview : !!src
  const handleInsert = () => {
    if (!canInsert) return
    const finalSrc = tab === 'upload' ? preview : src
    onConfirm(finalSrc, alt)
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-xl p-5 w-[420px] max-w-[95vw] flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <p className="text-sm font-semibold text-gray-800">Insert Image</p>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600 text-lg leading-none">✕</button>
        </div>

        {/* Tabs */}
        <div className="flex rounded-md border border-gray-200 overflow-hidden text-sm">
          {['upload', 'url'].map(t => (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              className={`flex-1 py-1.5 transition ${
                tab === t
                  ? 'bg-[#358ECD] text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              {t === 'upload' ? 'Browse / Upload' : 'Image URL'}
            </button>
          ))}
        </div>

        {/* Upload tab */}
        {tab === 'upload' && (
          <div
            onDragOver={e => e.preventDefault()}
            onDrop={handleDrop}
            className="border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center gap-3 p-4 text-center min-h-[160px] hover:border-[#358ECD] transition cursor-pointer"
            onClick={() => document.getElementById('rce-img-upload').click()}
          >
            <input
              id="rce-img-upload"
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleFileChange}
            />
            {preview ? (
              <>
                <img src={preview} alt="preview" className="max-h-32 max-w-full rounded object-contain" />
                <p className="text-xs text-gray-500 truncate max-w-full">{fileName}</p>
                <button
                  type="button"
                  onClick={e => { e.stopPropagation(); setPreview(null); setFileName('') }}
                  className="text-xs text-red-500 hover:underline"
                >
                  Remove
                </button>
              </>
            ) : (
              <>
                <span className="text-3xl text-gray-300">🖼</span>
                <p className="text-sm text-gray-500">Click to browse or drag an image here</p>
                <p className="text-xs text-gray-400">PNG, JPG, GIF, WebP supported</p>
              </>
            )}
          </div>
        )}

        {/* URL tab */}
        {tab === 'url' && (
          <div className="flex flex-col gap-2">
            <input
              autoFocus
              type="url"
              value={src}
              onChange={e => setSrc(e.target.value)}
              placeholder="https://example.com/image.png"
              className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#358ECD]"
            />
            {src && (
              <img
                src={src}
                alt="preview"
                onError={e => { e.target.style.display = 'none' }}
                className="max-h-32 rounded object-contain border border-gray-100"
              />
            )}
          </div>
        )}

        {/* Alt text */}
        <input
          type="text"
          value={alt}
          onChange={e => setAlt(e.target.value)}
          placeholder="Alt text (optional)"
          className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#358ECD]"
        />

        <div className="flex gap-2 justify-end">
          <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm rounded border border-gray-300 hover:bg-gray-50">Cancel</button>
          <button
            type="button"
            onClick={handleInsert}
            disabled={!canInsert}
            className="px-4 py-1.5 text-sm rounded bg-[#358ECD] text-white hover:bg-[#2d7ab5] disabled:opacity-40"
          >
            Insert Image
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Source HTML modal ────────────────────────────────────────────────────────
function SourceModal({ html, onConfirm, onClose }) {
  const [src, setSrc] = useState(html)
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-xl p-4 w-[640px] max-w-[95vw]">
        <p className="text-sm font-semibold text-gray-700 mb-2">Edit HTML Source</p>
        <textarea
          autoFocus
          value={src}
          onChange={e => setSrc(e.target.value)}
          rows={12}
          className="w-full border border-gray-300 rounded px-3 py-2 text-xs font-mono mb-3 focus:outline-none focus:ring-2 focus:ring-[#358ECD] resize-y"
        />
        <div className="flex gap-2 justify-end">
          <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm rounded border border-gray-300 hover:bg-gray-50">Cancel</button>
          <button type="button" onClick={() => onConfirm(src)} className="px-3 py-1.5 text-sm rounded bg-[#358ECD] text-white hover:bg-[#2d7ab5]">Apply</button>
        </div>
      </div>
    </div>
  )
}

// ─── Main Editor component ────────────────────────────────────────────────────
export function RichContentEditor({ initialContent = '', onChange, readOnly = false }) {
  const [showLinkModal, setShowLinkModal] = useState(false)
  const [showImageModal, setShowImageModal] = useState(false)
  const [showSourceModal, setShowSourceModal] = useState(false)

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Link.configure({ openOnClick: false }),
      Image,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      TextStyle,
      Color,
      Highlight.configure({ multicolor: true }),
      Placeholder.configure({ placeholder: 'Start typing your content here…' }),
      Table.configure({ resizable: true }),
      TableRow,
      TableCell,
      TableHeader,
    ],
    content: initialContent,
    editable: !readOnly,
    onUpdate: ({ editor }) => {
      onChange?.(editor.getHTML())
    },
  })

  // Sync initial content when it changes externally (modal re-open)
  useEffect(() => {
    if (editor && initialContent !== editor.getHTML()) {
      editor.commands.setContent(initialContent, false)
    }
  }, [initialContent]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleLinkConfirm = useCallback((href) => {
    setShowLinkModal(false)
    if (!editor) return
    if (!href) {
      editor.chain().focus().unsetLink().run()
    } else {
      editor.chain().focus().extendMarkRange('link').setLink({ href }).run()
    }
  }, [editor])

  const handleImageConfirm = useCallback((src, alt) => {
    setShowImageModal(false)
    editor?.chain().focus().setImage({ src, alt }).run()
  }, [editor])

  const handleSourceConfirm = useCallback((html) => {
    setShowSourceModal(false)
    editor?.commands.setContent(html, true)
  }, [editor])

  if (!editor) return null

  const currentLinkHref = editor.getAttributes('link').href || ''

  return (
    <div className="border border-gray-300 rounded-lg overflow-hidden flex flex-col">
      {!readOnly && (
        <div className="flex flex-wrap items-center gap-0.5 px-2 py-1.5 border-b border-gray-200 bg-gray-50">
          {/* Headings */}
          <select
            value={editor.isActive('heading', { level: 1 }) ? '1' : editor.isActive('heading', { level: 2 }) ? '2' : editor.isActive('heading', { level: 3 }) ? '3' : '0'}
            onChange={e => {
              const v = Number(e.target.value)
              if (v === 0) editor.chain().focus().setParagraph().run()
              else editor.chain().focus().toggleHeading({ level: v }).run()
            }}
            className="text-sm border border-gray-300 rounded px-1 py-0.5 bg-white text-gray-700 focus:outline-none focus:ring-1 focus:ring-[#358ECD]"
          >
            <option value="0">Paragraph</option>
            <option value="1">Heading 1</option>
            <option value="2">Heading 2</option>
            <option value="3">Heading 3</option>
          </select>

          <Divider />

          {/* Inline formatting */}
          <ToolBtn onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive('bold')} title="Bold (Ctrl+B)"><b>B</b></ToolBtn>
          <ToolBtn onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive('italic')} title="Italic (Ctrl+I)"><i>I</i></ToolBtn>
          <ToolBtn onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive('underline')} title="Underline (Ctrl+U)"><u>U</u></ToolBtn>
          <ToolBtn onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive('strike')} title="Strikethrough"><s>S</s></ToolBtn>
          <ToolBtn onClick={() => editor.chain().focus().toggleCode().run()} active={editor.isActive('code')} title="Inline code">{'<>'}</ToolBtn>
          <ToolBtn onClick={() => editor.chain().focus().toggleHighlight().run()} active={editor.isActive('highlight')} title="Highlight">H</ToolBtn>

          <Divider />

          {/* Alignment */}
          <ToolBtn onClick={() => editor.chain().focus().setTextAlign('left').run()} active={editor.isActive({ textAlign: 'left' })} title="Align left">≡</ToolBtn>
          <ToolBtn onClick={() => editor.chain().focus().setTextAlign('center').run()} active={editor.isActive({ textAlign: 'center' })} title="Align centre">≡</ToolBtn>
          <ToolBtn onClick={() => editor.chain().focus().setTextAlign('right').run()} active={editor.isActive({ textAlign: 'right' })} title="Align right">≡</ToolBtn>

          <Divider />

          {/* Lists */}
          <ToolBtn onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive('bulletList')} title="Bullet list">• —</ToolBtn>
          <ToolBtn onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive('orderedList')} title="Numbered list">1.</ToolBtn>
          <ToolBtn onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive('blockquote')} title="Blockquote">"</ToolBtn>
          <ToolBtn onClick={() => editor.chain().focus().toggleCodeBlock().run()} active={editor.isActive('codeBlock')} title="Code block">{'{ }'}</ToolBtn>

          <Divider />

          {/* Link & Image */}
          <ToolBtn onClick={() => setShowLinkModal(true)} active={editor.isActive('link')} title="Insert/edit link">🔗</ToolBtn>
          <ToolBtn onClick={() => setShowImageModal(true)} active={false} title="Insert image">🖼</ToolBtn>

          <Divider />

          {/* Table */}
          <ToolBtn
            onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}
            active={false}
            title="Insert table"
          >⊞</ToolBtn>

          <Divider />

          {/* Undo / Redo */}
          <ToolBtn onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title="Undo">↩</ToolBtn>
          <ToolBtn onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title="Redo">↪</ToolBtn>

          <Divider />

          {/* Source HTML */}
          <ToolBtn onClick={() => setShowSourceModal(true)} active={false} title="Edit HTML source">{'</>'}</ToolBtn>
        </div>
      )}

      <EditorContent
        editor={editor}
        className="prose prose-sm max-w-none p-4 min-h-[180px] focus:outline-none overflow-auto rich-content-render"
      />

      {showLinkModal && (
        <LinkModal initial={currentLinkHref} onConfirm={handleLinkConfirm} onClose={() => setShowLinkModal(false)} />
      )}
      {showImageModal && (
        <ImageModal onConfirm={handleImageConfirm} onClose={() => setShowImageModal(false)} />
      )}
      {showSourceModal && (
        <SourceModal html={editor.getHTML()} onConfirm={handleSourceConfirm} onClose={() => setShowSourceModal(false)} />
      )}
    </div>
  )
}

// ─── Edit Content Modal (shown in customize mode) ─────────────────────────────
export function RichContentEditorModal({ widget, onClose, onSaved }) {
  const [content, setContent] = useState(widget?.rich_content || '')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const masterWidgetId = widget?.master_widget_id

  const handleSave = async () => {
    if (!masterWidgetId) {
      setError('No master widget ID — cannot save.')
      return
    }
    setSaving(true)
    setError('')
    try {
      await dashboardApi.updateMasterWidget(masterWidgetId, { rich_content: content })
      onSaved?.(content)
      onClose()
    } catch (err) {
      setError(err?.response?.data?.detail || 'Save failed. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl shadow-2xl flex flex-col w-full max-w-3xl max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
          <h2 className="text-base font-semibold text-gray-800">
            Edit Rich Content — <span className="text-[#358ECD]">{widget?.title || 'Widget'}</span>
          </h2>
          <button type="button" onClick={onClose} className="p-1.5 rounded hover:bg-gray-100 text-gray-500">✕</button>
        </div>

        {/* Editor */}
        <div className="flex-1 overflow-auto p-4">
          <RichContentEditor
            initialContent={content}
            onChange={setContent}
          />
          {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-5 py-3 border-t border-gray-200 bg-gray-50">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm rounded-md border border-gray-300 hover:bg-gray-100">
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="px-5 py-2 text-sm rounded-md bg-[#358ECD] text-white hover:bg-[#2d7ab5] disabled:opacity-60 flex items-center gap-2"
          >
            {saving ? (
              <><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin inline-block" />Saving…</>
            ) : 'Save Content'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default RichContentEditor
